import { QuizResultFactory, PuzzleAttemptFactory, PuzzleStatus, } from "../../types/quiz";
import { TopicAssignmentFactory, } from "../../types/assignment";
import { ContentStatus } from "../../types/topics";
import { PUZZLE_COLOR_LEVELS } from "../../pgn/PgnParser";
/**
 * In-memory implementation of ProgressionService for testing.
 */
export class ProgressionServiceLocal {
    quizResults = new Map();
    puzzleAttempts = new Map();
    topicAssignments = new Map();
    // ============ Quiz Results Operations ============
    async saveQuizResult(quizResultData) {
        const quizResult = QuizResultFactory.create(quizResultData);
        this.quizResults.set(quizResult.id, quizResult);
        // Update assignment metrics if this quiz is for an assigned topic
        if (quizResult.topicId) {
            const score = Math.round((quizResult.correctCount / quizResult.totalScenarios) * 100);
            await this.updateAssignmentMetrics(quizResult.playerId, quizResult.topicId, {
                quizPersonalBest: score,
                quizAttempts: 1,
            });
        }
        // Create individual puzzle attempts for each puzzle in the quiz
        const puzzleAttempts = quizResult.puzzleResults.map((puzzleResult) => {
            // Create a minimal Scenario object from available data
            // Note: We use object literal instead of class instance to avoid needing the update() method
            const scenario = {
                id: puzzleResult.scenarioId,
                topicId: quizResult.topicId || "",
                topicTitle: quizResult.topicTitle || null,
                title: puzzleResult.scenarioTitle || "",
                isPuzzle: true,
                isHomework: false,
                pgn: puzzleResult.pgn,
                status: ContentStatus.Published,
                order: 0,
                createdAt: new Date(),
                updatedAt: new Date(),
                update: () => { }, // Dummy method to satisfy Scenario interface
            };
            return {
                scenarioId: puzzleResult.scenarioId,
                scenarioTitle: puzzleResult.scenarioTitle,
                pgn: puzzleResult.pgn,
                playerId: quizResult.playerId,
                studentName: quizResult.studentName,
                scenario,
                status: puzzleResult.status,
                answerPgn: puzzleResult.answerPgn,
                attemptCount: puzzleResult.attemptCount || 1,
                timeSpent: puzzleResult.timeSpent,
                quizResultId: quizResult.id,
                lectureId: quizResult.lectureId,
                classroomId: quizResult.classroomId,
                year: quizResult.year,
                term: quizResult.term,
                attemptedAt: quizResult.completedAt || quizResult.startedAt,
            };
        });
        await this.savePuzzleAttemptsBatch(puzzleAttempts);
        return quizResult;
    }
    async getQuizResult(_playerId, quizResultId) {
        return this.quizResults.get(quizResultId) || null;
    }
    async getQuizResultsByStudent(playerId, limit = 50) {
        return Array.from(this.quizResults.values())
            .filter((qr) => qr.playerId === playerId)
            .sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime())
            .slice(0, limit);
    }
    async getQuizResultsByTopic(topicId, limit = 50) {
        return Array.from(this.quizResults.values())
            .filter((qr) => qr.topicId === topicId)
            .sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime())
            .slice(0, limit);
    }
    async getQuizResultsByLecture(lectureId) {
        return Array.from(this.quizResults.values()).filter((qr) => qr.lectureId === lectureId);
    }
    async getQuizResultsByClassroom(classroomId, year, term) {
        return Array.from(this.quizResults.values()).filter((qr) => {
            if (qr.classroomId !== classroomId)
                return false;
            if (year && qr.year !== year)
                return false;
            if (term && qr.term !== term)
                return false;
            return true;
        });
    }
    // ============ Puzzle Attempts Operations ============
    async savePuzzleAttempt(attemptData) {
        const attempt = PuzzleAttemptFactory.create(attemptData);
        // Save the attempt
        this.puzzleAttempts.set(attempt.id, attempt);
        // Update assignment metrics if this puzzle is for an assigned topic
        if (attempt.scenario?.topicId &&
            attempt.scenario?.isHomework &&
            !attempt.quizResultId // Only count standalone puzzles, not quiz puzzles
        ) {
            // Get the assignment to check completed puzzle IDs
            const assignment = Array.from(this.topicAssignments.values()).find((a) => a.playerId === attempt.playerId &&
                a.topicId === attempt.scenario.topicId);
            if (assignment) {
                const completedPuzzleIds = assignment.completedPuzzleIds || [];
                const isNewCompletion = attempt.status === PuzzleStatus.Correct &&
                    !completedPuzzleIds.includes(attempt.scenarioId);
                // Always increment puzzle attempts
                await this.updateAssignmentMetrics(attempt.playerId, attempt.scenario.topicId, { puzzleAttempts: 1 });
                // If this is a new correct completion, add to completed list
                if (isNewCompletion) {
                    assignment.completedPuzzleIds = [
                        ...completedPuzzleIds,
                        attempt.scenarioId,
                    ];
                    assignment.completedPuzzles = assignment.completedPuzzleIds.length;
                    assignment.updatedAt = new Date();
                }
            }
        }
        return attempt;
    }
    async savePuzzleAttemptsBatch(attemptsData) {
        const attempts = [];
        for (const attemptData of attemptsData) {
            const attempt = await this.savePuzzleAttempt(attemptData);
            attempts.push(attempt);
        }
        return attempts;
    }
    async getPuzzleAttempt(_playerId, attemptId) {
        return this.puzzleAttempts.get(attemptId) || null;
    }
    async getPuzzleAttemptsByStudent(playerId, limit = 50) {
        return Array.from(this.puzzleAttempts.values())
            .filter((pa) => pa.playerId === playerId)
            .sort((a, b) => b.attemptedAt.getTime() - a.attemptedAt.getTime())
            .slice(0, limit);
    }
    async hasStudentCompletedPuzzle(playerId, scenarioId) {
        return Array.from(this.puzzleAttempts.values()).some((pa) => pa.playerId === playerId &&
            pa.scenarioId === scenarioId &&
            pa.status === PuzzleStatus.Correct);
    }
    async getCompletedPuzzlesByStudent(playerId) {
        return Array.from(this.puzzleAttempts.values())
            .filter((pa) => pa.playerId === playerId && pa.status === PuzzleStatus.Correct)
            .sort((a, b) => b.attemptedAt.getTime() - a.attemptedAt.getTime());
    }
    async getPuzzleAttemptsByScenario(scenarioId) {
        return Array.from(this.puzzleAttempts.values()).filter((pa) => pa.scenarioId === scenarioId);
    }
    async getUncompletedScenarioIds(playerId, scenarioIds) {
        if (scenarioIds.length === 0) {
            return [];
        }
        const completedPuzzles = await this.getCompletedPuzzlesByStudent(playerId);
        const completedScenarioIds = new Set(completedPuzzles.map((p) => p.scenarioId));
        return scenarioIds.filter((id) => !completedScenarioIds.has(id));
    }
    async getStudentProgressByTopic(playerId, topicId) {
        const attempts = Array.from(this.puzzleAttempts.values())
            .filter((pa) => pa.playerId === playerId && pa.scenario.topicId === topicId)
            .sort((a, b) => b.attemptedAt.getTime() - a.attemptedAt.getTime());
        if (attempts.length === 0) {
            return {
                playerId,
                topicId,
                topicTitle: "",
                totalScenarios: 0,
                completedScenarios: 0,
                correctScenarios: 0,
                completionRate: 0,
                successRate: 0,
                attempts: [],
            };
        }
        const topicTitle = attempts[0].scenario.title;
        const uniqueScenarios = new Set(attempts.map((a) => a.scenarioId));
        const correctScenarios = new Set(attempts
            .filter((a) => a.status === PuzzleStatus.Correct)
            .map((a) => a.scenarioId));
        return {
            playerId,
            studentName: attempts[0].studentName,
            topicId,
            topicTitle,
            totalScenarios: uniqueScenarios.size,
            completedScenarios: correctScenarios.size,
            correctScenarios: correctScenarios.size,
            completionRate: (correctScenarios.size / uniqueScenarios.size) * 100,
            successRate: (correctScenarios.size / uniqueScenarios.size) * 100,
            attempts,
            lastAttemptedAt: attempts[0].attemptedAt,
        };
    }
    // ============ Analytics & Reports Operations ============
    async getStudentProgressSummary(playerId, topicService // Optional TopicService to get total puzzles per topic
    ) {
        const attempts = Array.from(this.puzzleAttempts.values())
            .filter((pa) => pa.playerId === playerId)
            .sort((a, b) => b.attemptedAt.getTime() - a.attemptedAt.getTime());
        // Get quiz results for quiz statistics
        const quizResults = await this.getQuizResultsByStudent(playerId, 1000);
        const totalQuizzesTaken = quizResults.length;
        const averageQuizScore = totalQuizzesTaken > 0
            ? quizResults.reduce((sum, quiz) => {
                const score = quiz.totalScenarios > 0
                    ? (quiz.correctCount / quiz.totalScenarios) * 100
                    : 0;
                return sum + score;
            }, 0) / totalQuizzesTaken
            : 0;
        if (attempts.length === 0) {
            return {
                playerId,
                totalPuzzlesAttempted: 0,
                totalPuzzlesCorrect: 0,
                totalPuzzlesIncorrect: 0,
                totalPuzzlesSkipped: 0,
                totalQuizzesTaken,
                averageQuizScore,
                byTopic: {},
                recentAttempts: [],
            };
        }
        const studentName = attempts[0].studentName;
        const correct = attempts.filter((a) => a.status === PuzzleStatus.Correct);
        const incorrect = attempts.filter((a) => a.status === PuzzleStatus.Incorrect);
        const skipped = attempts.filter((a) => a.status === PuzzleStatus.Skipped);
        // Calculate by topic
        const byTopic = {};
        const topicMap = new Map();
        for (const attempt of attempts) {
            if (!topicMap.has(attempt.scenario.topicId)) {
                topicMap.set(attempt.scenario.topicId, []);
            }
            topicMap.get(attempt.scenario.topicId).push(attempt);
        }
        // Get puzzle counts: use homeworkCount for assigned topics, puzzleCount for others
        const topicPuzzleCounts = new Map();
        if (topicMap.size > 0) {
            try {
                const assignments = await this.getStudentAssignments(playerId);
                const assignedTopicIds = new Set(assignments.filter((a) => a.isActive).map((a) => a.topicId));
                const topicIds = Array.from(topicMap.keys());
                if (topicService) {
                    const topics = await topicService.getTopicsByIds(topicIds);
                    const topicsById = new Map(topics.map((t) => [t.id, t]));
                    for (const topicId of topicIds) {
                        const topic = topicsById.get(topicId);
                        if (!topic)
                            continue;
                        if (assignedTopicIds.has(topicId)) {
                            if (topic.homeworkCount !== undefined) {
                                topicPuzzleCounts.set(topicId, topic.homeworkCount);
                            }
                            else {
                                const scenarios = await topicService.getScenarios(topicId);
                                const hwCount = scenarios.filter((s) => s.isHomework).length;
                                topicPuzzleCounts.set(topicId, hwCount);
                            }
                        }
                        else {
                            if (topic.puzzleCount !== undefined) {
                                topicPuzzleCounts.set(topicId, topic.puzzleCount);
                            }
                            else {
                                const scenarios = await topicService.getScenarios(topicId);
                                const puzzleCount = scenarios.filter((s) => s.isPuzzle).length;
                                topicPuzzleCounts.set(topicId, puzzleCount);
                            }
                        }
                    }
                }
            }
            catch (error) {
                console.error("Error fetching topic puzzle counts:", error);
            }
        }
        for (const [topicId, topicAttempts] of topicMap.entries()) {
            const topicCorrect = topicAttempts.filter((a) => a.status === PuzzleStatus.Correct);
            // Calculate unique puzzles attempted and completed
            const uniqueScenarioIds = new Set(topicAttempts.map((a) => a.scenarioId).filter((id) => id));
            const completedScenarioIds = new Set(topicCorrect.map((a) => a.scenarioId).filter((id) => id));
            const attemptedPuzzles = uniqueScenarioIds.size;
            const completedPuzzles = completedScenarioIds.size;
            const totalPuzzles = topicPuzzleCounts.get(topicId) || attemptedPuzzles;
            const notAttemptedPuzzles = Math.max(0, totalPuzzles - attemptedPuzzles);
            byTopic[topicId] = {
                topicTitle: topicAttempts[0].scenario.title,
                totalPuzzles,
                completedPuzzles,
                attemptedPuzzles,
                notAttemptedPuzzles,
                attempted: topicAttempts.length,
                correct: topicCorrect.length,
            };
        }
        return {
            playerId,
            studentName,
            totalPuzzlesAttempted: attempts.length,
            totalPuzzlesCorrect: correct.length,
            totalPuzzlesIncorrect: incorrect.length,
            totalPuzzlesSkipped: skipped.length,
            totalQuizzesTaken,
            averageQuizScore,
            byTopic,
            recentAttempts: attempts.slice(0, 20),
            lastAttemptedAt: attempts[0].attemptedAt,
        };
    }
    async getPuzzleStatistics(scenarioId) {
        const attempts = await this.getPuzzleAttemptsByScenario(scenarioId);
        if (attempts.length === 0) {
            return {
                scenarioId,
                scenarioTitle: "",
                topicId: "",
                topicTitle: "",
                totalAttempts: 0,
                uniqueStudents: 0,
                correctAttempts: 0,
                incorrectAttempts: 0,
                skippedAttempts: 0,
                successRate: 0,
                averageAttempts: 0,
            };
        }
        const uniqueStudents = new Set(attempts.map((a) => a.playerId));
        const correct = attempts.filter((a) => a.status === PuzzleStatus.Correct);
        const incorrect = attempts.filter((a) => a.status === PuzzleStatus.Incorrect);
        const skipped = attempts.filter((a) => a.status === PuzzleStatus.Skipped);
        const totalAttemptCount = attempts.reduce((sum, a) => sum + a.attemptCount, 0);
        const attemptsWithTime = attempts.filter((a) => a.timeSpent !== undefined);
        const totalTime = attemptsWithTime.reduce((sum, a) => sum + (a.timeSpent || 0), 0);
        return {
            scenarioId,
            scenarioTitle: attempts[0].scenarioTitle,
            topicId: attempts[0].scenario.topicId,
            topicTitle: attempts[0].scenario.title,
            totalAttempts: attempts.length,
            uniqueStudents: uniqueStudents.size,
            correctAttempts: correct.length,
            incorrectAttempts: incorrect.length,
            skippedAttempts: skipped.length,
            successRate: (correct.length / attempts.length) * 100,
            averageAttempts: totalAttemptCount / attempts.length,
            averageTimeSpent: attemptsWithTime.length > 0
                ? totalTime / attemptsWithTime.length
                : undefined,
            difficultyLevel: attempts[0].scenario.difficultyLevel,
            puzzleLevel: attempts[0].scenario.puzzleLevel
                ? PUZZLE_COLOR_LEVELS[attempts[0].scenario.puzzleLevel]
                : undefined,
        };
    }
    // ============ Test Helper Methods ============
    /**
     * Clear all data (for testing purposes)
     */
    clear() {
        this.quizResults.clear();
        this.puzzleAttempts.clear();
    }
    /**
     * Get all quiz results (for testing purposes)
     */
    getAllQuizResults() {
        return Array.from(this.quizResults.values());
    }
    /**
     * Get all puzzle attempts (for testing purposes)
     */
    getAllPuzzleAttempts() {
        return Array.from(this.puzzleAttempts.values());
    }
    // ============ Topic Assignment Operations ============
    async assignTopic(assignmentData) {
        // Use topicId as document ID to match Firestore behavior
        const key = `${assignmentData.playerId}-${assignmentData.topicId}`;
        // Check if assignment already exists
        const existingAssignment = this.topicAssignments.get(key);
        if (existingAssignment) {
            // Merge with existing assignment to preserve progress, update metadata
            const mergedAssignment = {
                ...existingAssignment,
                // Update metadata fields from new data
                studentName: assignmentData.studentName,
                topicTitle: assignmentData.topicTitle,
                teacherId: assignmentData.teacherId,
                teacherName: assignmentData.teacherName,
                lectureId: assignmentData.lectureId,
                classroomId: assignmentData.classroomId,
                year: assignmentData.year,
                term: assignmentData.term,
                assignedAt: assignmentData.assignedAt,
                isActive: assignmentData.isActive,
                totalPuzzles: assignmentData.totalPuzzles,
                updatedAt: new Date(),
                // Preserve existing progress fields (don't overwrite)
                // quizPersonalBest, quizAttempts, puzzleAttempts, completedPuzzles, completedPuzzleIds, videoWatchCount
            };
            this.topicAssignments.set(key, mergedAssignment);
            return mergedAssignment;
        }
        else {
            // Create new assignment
            const assignment = TopicAssignmentFactory.create(assignmentData, assignmentData.topicId // Use topicId as the ID
            );
            this.topicAssignments.set(key, assignment);
            return assignment;
        }
    }
    async getStudentAssignments(playerId) {
        return Array.from(this.topicAssignments.values())
            .filter((a) => a.playerId === playerId)
            .sort((a, b) => b.assignedAt.getTime() - a.assignedAt.getTime());
    }
    async getTopicAssignment(playerId, topicId) {
        // Use playerId-topicId as key to match Firestore structure
        const key = `${playerId}-${topicId}`;
        const assignment = this.topicAssignments.get(key);
        return assignment || null;
    }
    async updateAssignmentMetrics(playerId, topicId, metrics) {
        // Use playerId-topicId as key to match Firestore structure
        const key = `${playerId}-${topicId}`;
        const assignment = this.topicAssignments.get(key);
        if (!assignment) {
            return; // No assignment found, nothing to update
        }
        // Update isActive status
        if (metrics.isActive !== undefined) {
            assignment.isActive = metrics.isActive;
        }
        // Only update quiz personal best if the new score is higher
        if (metrics.quizPersonalBest !== undefined) {
            const currentBest = assignment.quizPersonalBest || 0;
            if (metrics.quizPersonalBest > currentBest) {
                assignment.quizPersonalBest = metrics.quizPersonalBest;
            }
        }
        // Increment quiz attempts count
        if (metrics.quizAttempts !== undefined) {
            assignment.quizAttempts =
                (assignment.quizAttempts || 0) + metrics.quizAttempts;
        }
        // Increment puzzle attempts count
        if (metrics.puzzleAttempts !== undefined) {
            assignment.puzzleAttempts =
                (assignment.puzzleAttempts || 0) + metrics.puzzleAttempts;
        }
        // Set completed puzzles count (not increment, since it's derived from array length)
        if (metrics.completedPuzzles !== undefined) {
            assignment.completedPuzzles = metrics.completedPuzzles;
        }
        // Increment video watch count
        if (metrics.videoWatchCount !== undefined) {
            assignment.videoWatchCount =
                (assignment.videoWatchCount || 0) + metrics.videoWatchCount;
        }
        assignment.updatedAt = new Date();
    }
    async hasTopicAssignment(playerId, topicId) {
        // Use playerId-topicId as key to match Firestore structure
        const key = `${playerId}-${topicId}`;
        return this.topicAssignments.has(key);
    }
    async getAssignmentsByStudentsAndTopics(playerIds, topicIds) {
        const assignmentsMap = new Map();
        Array.from(this.topicAssignments.values()).forEach((assignment) => {
            if (playerIds.includes(assignment.playerId) &&
                topicIds.includes(assignment.topicId)) {
                const key = `${assignment.playerId}-${assignment.topicId}`;
                assignmentsMap.set(key, assignment);
            }
        });
        return assignmentsMap;
    }
    async getAssignmentsByLecture(lectureId) {
        const assignmentsMap = new Map();
        Array.from(this.topicAssignments.values()).forEach((assignment) => {
            if (assignment.lectureId === lectureId) {
                const key = `${assignment.playerId}-${assignment.topicId}`;
                assignmentsMap.set(key, assignment);
            }
        });
        return assignmentsMap;
    }
    // ============ Batch Assignment Operations ============
    async assignTopicsToStudents(params) {
        const { students, topics, teacher, lecture } = params;
        // Filter students who have chessSquadUsername
        const eligibleStudents = students.filter((s) => s.chessSquadUsername);
        if (eligibleStudents.length === 0) {
            return { assignCount: 0, studentCount: 0 };
        }
        // Extract teacher info
        const teacherId = teacher.teacherId;
        const teacherName = `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim();
        // Create assignment operations
        const operations = [];
        for (const student of eligibleStudents) {
            for (const topic of topics) {
                operations.push(this.assignTopic({
                    playerId: student.chessSquadUsername,
                    studentName: `${student.firstName} ${student.lastName}`.trim(),
                    topicId: topic.id,
                    topicTitle: topic.title,
                    teacherId,
                    teacherName,
                    lectureId: lecture.id,
                    classroomId: lecture.classroomId,
                    year: lecture.year,
                    term: lecture.term,
                    assignedAt: new Date(),
                    isActive: true,
                    totalPuzzles: topic.homeworkCount || 0,
                }));
            }
        }
        // Execute all assignment operations
        await Promise.all(operations);
        return {
            assignCount: operations.length,
            studentCount: eligibleStudents.length,
        };
    }
    async unassignTopicsFromStudents(assignments) {
        const operations = [];
        for (const { playerId, topicId } of assignments) {
            operations.push(this.updateAssignmentMetrics(playerId, topicId, {
                isActive: false,
            }));
        }
        await Promise.all(operations);
        return {
            unassignCount: operations.length,
        };
    }
}
